import { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import toast, { Toaster } from "react-hot-toast";
import RazorpayButton from "../components/RazorpayButton";
import {
  X, Upload, CheckCircle, ArrowRight, ArrowLeft, Award
} from "lucide-react";

const CLOUD_NAME    = "dan0bapfg";
const UPLOAD_PRESET = "htst_event";
const MAX_SIZE_KB   = 150;

const ANNUAL_FEES = {
  volunteer: 501,
  intern:    501,
  member:    1100,
};

const PAYMENT_BUTTON_IDS = {
  volunteer: "pl_TdXaVyv9wdkrhe",
  intern:    "pl_TdXaVyv9wdkrhe", // Intern shares Volunteer's ₹501 button, per spec
  member:    "pl_TdXdZOggUkEMdV",
};

const TYPE_LABELS = { volunteer: "Volunteer", intern: "Intern", member: "Member" };

async function uploadToCloudinary(file, folder) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", UPLOAD_PRESET);
  formData.append("folder", `htst_registrations/${folder}`);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
    method: "POST", body: formData,
  });
  if (!res.ok) throw new Error("Upload failed");
  const data = await res.json();
  return data.secure_url;
}

function useFileUpload() {
  const [file, setFile]       = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError]     = useState("");

  function handleChange(e) {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { setError("Only image files allowed"); return; }
    if (f.size > MAX_SIZE_KB * 1024) {
      setError(`Max size is ${MAX_SIZE_KB}KB. This file is ${Math.round(f.size / 1024)}KB`);
      return;
    }
    setError("");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  }
  function clear() { setFile(null); setPreview(null); setError(""); }
  return { file, preview, error, handleChange, clear };
}

const EMPTY_FORM = {
  fullName: "", guardianName: "", mobile: "", whatsapp: "", email: "",
  dob: "", gender: "", address: "", city: "", state: "", pincode: "",
  bloodGroup: "", aadharNumber: "", refName: "", refContact: "",
};

export default function VolunteerRegistrationModal({ defaultType = "volunteer", onClose }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [type, setType] = useState(defaultType);
  const [agreed, setAgreed] = useState(false);
  const [paidConfirmed, setPaidConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const aadharFront = useFileUpload();
  const aadharBack  = useFileUpload();
  const photo       = useFileUpload();

  const fee = ANNUAL_FEES[type];

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  function validateStep1() {
    const required = ["fullName","guardianName","mobile","whatsapp","email","dob","address","aadharNumber"];
    for (const k of required) {
      if (!form[k]?.trim()) { toast.error("Please fill all required fields (*)"); return false; }
    }
    if (!/^\d{10}$/.test(form.mobile)) { toast.error("Mobile number must be 10 digits"); return false; }
    if (!/^\d{12}$/.test(form.aadharNumber.replace(/\s/g, ""))) { toast.error("Aadhaar number must be 12 digits"); return false; }
    return true;
  }

  function validateStep2() {
    if (!aadharFront.file) { toast.error("Please upload Aadhaar front"); return false; }
    if (!aadharBack.file)  { toast.error("Please upload Aadhaar back"); return false; }
    if (!photo.file)       { toast.error("Please upload your photo"); return false; }
    return true;
  }

  function nextStep() {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep(s => s + 1);
  }

  async function handleSubmit() {
    if (!agreed) return toast.error("Please agree to the terms before submitting");
    if (!paidConfirmed) return toast.error(`Please confirm you have completed the ₹${fee}/year payment`);

    setSubmitting(true);
    try {
      toast.loading("Uploading documents...", { id: "reg-upload" });
      const aadharFrontUrl = await uploadToCloudinary(aadharFront.file, "aadhar");
      const aadharBackUrl  = await uploadToCloudinary(aadharBack.file, "aadhar");
      const photoUrl       = await uploadToCloudinary(photo.file, "photo");
      toast.dismiss("reg-upload");

      await addDoc(collection(db, "registrations"), {
        type,
        amountPaid: fee,
        ...form,
        aadharFrontUrl,
        aadharBackUrl,
        photoUrl,
        status: "pending",
        paymentConfirmed: false,
        createdAt: serverTimestamp(),
      });

      toast.success("Application submitted!");
      setDone(true);
    } catch (e) {
      console.error(e);
      toast.error("Submission failed: " + e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="reg-overlay" onClick={onClose}>
      <Toaster position="top-center" />
      <style>{`
        .reg-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.9); z-index:10002; display:flex; align-items:center; justify-content:center; padding:16px; backdrop-filter:blur(10px); }
        .reg-modal { background:#111009; border:1px solid rgba(255,255,255,0.1); border-radius:20px; padding:0; width:100%; max-width:640px; max-height:92vh; overflow-y:auto; position:relative; }
        .reg-close { position:absolute; top:14px; right:14px; background:rgba(255,255,255,0.08); border:1px solid rgba(255,255,255,0.1); border-radius:50%; width:34px; height:34px; display:flex; align-items:center; justify-content:center; cursor:pointer; color:#a8a29e; z-index:2; }
        .reg-header { padding:28px 28px 0; }
        .reg-title { font-family:'Playfair Display',serif; font-size:24px; font-weight:800; color:#fef3e2; margin:0 0 6px; }
        .reg-sub { font-size:13px; color:#78716c; margin:0 0 20px; }
        .reg-steps { display:flex; gap:6px; padding:0 28px 20px; }
        .reg-step-dot { flex:1; height:3px; border-radius:2px; background:rgba(255,255,255,0.08); }
        .reg-step-dot.active { background:#f59e0b; }
        .reg-body { padding:0 28px 28px; }
        .reg-type-row { display:flex; gap:10px; margin-bottom:20px; }
        .reg-type-btn { flex:1; padding:12px; border-radius:10px; border:1.5px solid rgba(255,255,255,0.1); background:transparent; color:#78716c; font-weight:700; font-size:13px; cursor:pointer; font-family:inherit; transition:all 0.15s; }
        .reg-type-btn.active { border-color:#f59e0b; background:rgba(245,158,11,0.12); color:#f59e0b; }
        .reg-type-fee { display:block; font-size:10px; font-weight:600; opacity:0.8; margin-top:3px; }
        .reg-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
        .reg-field { margin-bottom:14px; }
        .reg-label { display:block; font-size:11px; font-weight:700; color:#78716c; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:6px; }
        .reg-input, .reg-select, .reg-textarea {
          width:100%; padding:10px 12px; background:#0a0908; border:1px solid rgba(255,255,255,0.1);
          border-radius:9px; color:#fef3e2; font-size:13px; font-family:inherit; outline:none; box-sizing:border-box;
        }
        .reg-nav-row { display:flex; gap:10px; margin-top:8px; }
        .reg-btn-primary { flex:1; display:flex; align-items:center; justify-content:center; gap:8px; padding:13px; background:#f59e0b; color:#0a0908; border:none; border-radius:11px; font-size:14px; font-weight:800; cursor:pointer; font-family:inherit; }
        .reg-btn-primary:disabled { opacity:0.6; cursor:not-allowed; }
        .reg-btn-ghost { padding:13px 20px; background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.1); border-radius:11px; color:#a8a29e; font-size:14px; font-weight:700; cursor:pointer; font-family:inherit; }
        .reg-upload-box { border:2px dashed rgba(255,255,255,0.1); border-radius:12px; cursor:pointer; overflow:hidden; background:rgba(255,255,255,0.02); min-height:100px; display:flex; align-items:center; justify-content:center; position:relative; transition:all 0.2s; }
        .reg-upload-box.has-file { border-color:#f59e0b; background:rgba(245,158,11,0.04); }
        .reg-upload-preview { width:100%; max-height:140px; object-fit:contain; display:block; }
        .reg-upload-label { font-size:11px; font-weight:700; color:#78716c; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:8px; }
        .reg-upload-error { font-size:11px; color:#ef4444; margin-top:6px; }
        .reg-benefit-box { background:rgba(245,158,11,0.06); border:1px solid rgba(245,158,11,0.2); border-radius:14px; padding:20px; margin-bottom:18px; }
        .reg-fee { font-family:'Playfair Display',serif; font-size:32px; font-weight:800; color:#f59e0b; margin-bottom:14px; }
        .reg-fee-type { font-size:13px; color:#78716c; font-weight:600; margin-top:-10px; margin-bottom:16px; }
        .reg-benefit-item { display:flex; align-items:center; gap:8px; font-size:13px; color:#a8a29e; margin-bottom:8px; }
        .reg-note-box { background:rgba(239,68,68,0.06); border:1px solid rgba(239,68,68,0.2); border-radius:12px; padding:16px; font-size:12.5px; color:#a8a29e; line-height:1.6; margin-bottom:16px; }
        .reg-checkbox-row { display:flex; align-items:flex-start; gap:10px; margin-bottom:16px; cursor:pointer; }
        .reg-pay-embed { margin-bottom:16px; display:flex; justify-content:center; }
        .reg-success { text-align:center; padding:20px; }
        @media(max-width:560px) { .reg-grid { grid-template-columns:1fr; } }
      `}</style>

      <div className="reg-modal" onClick={e => e.stopPropagation()}>
        <button className="reg-close" onClick={onClose}><X size={16} /></button>

        {done ? (
          <div className="reg-body reg-success" style={{ paddingTop: 60 }}>
            <CheckCircle size={48} color="#22c55e" style={{ marginBottom: 16 }} />
            <h2 className="reg-title">Application Submitted!</h2>
            <p style={{ color: "#78716c", fontSize: 14, lineHeight: 1.6, margin: "12px 0 24px" }}>
              Thank you for applying. Our team will verify your details and payment,
              and you'll receive an email once your application is reviewed.
            </p>
            <button className="reg-btn-primary" onClick={onClose} style={{ width: "auto", padding: "12px 28px", margin: "0 auto" }}>
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="reg-header">
              <h2 className="reg-title">Join HTS Trust</h2>
              <p className="reg-sub">Step {step} of 3 — {step === 1 ? "Your Details" : step === 2 ? "Document Upload" : "Payment & Confirmation"}</p>
            </div>
            <div className="reg-steps">
              {[1, 2, 3].map(n => <div key={n} className={`reg-step-dot${step >= n ? " active" : ""}`} />)}
            </div>

            <div className="reg-body">
              {/* ── STEP 1 ── */}
              {step === 1 && (
                <>
                  <div className="reg-type-row">
                    {Object.keys(TYPE_LABELS).map(t => (
                      <button key={t} className={`reg-type-btn${type === t ? " active" : ""}`} onClick={() => setType(t)}>
                        {TYPE_LABELS[t]}
                        <span className="reg-type-fee">₹{ANNUAL_FEES[t]}/year</span>
                      </button>
                    ))}
                  </div>

                  <div className="reg-grid">
                    <Field label="Full Name *" value={form.fullName} onChange={v => set("fullName", v)} />
                    <Field label="Father's / Husband's Name *" value={form.guardianName} onChange={v => set("guardianName", v)} />
                    <Field label="Mobile Number *" value={form.mobile} onChange={v => set("mobile", v)} placeholder="10-digit number" />
                    <Field label="WhatsApp Number *" value={form.whatsapp} onChange={v => set("whatsapp", v)} placeholder="10-digit number" />
                    <Field label="Email Address *" value={form.email} onChange={v => set("email", v)} type="email" />
                    <Field label="Date of Birth *" value={form.dob} onChange={v => set("dob", v)} type="date" />
                    <div className="reg-field">
                      <label className="reg-label">Gender</label>
                      <select className="reg-select" value={form.gender} onChange={e => set("gender", e.target.value)}>
                        <option value="">Select</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <Field label="Blood Group" value={form.bloodGroup} onChange={v => set("bloodGroup", v)} placeholder="O+" />
                  </div>

                  <div className="reg-field">
                    <label className="reg-label">Complete Address *</label>
                    <textarea className="reg-textarea" rows={2} value={form.address} onChange={e => set("address", e.target.value)} />
                  </div>

                  <div className="reg-grid">
                    <Field label="City" value={form.city} onChange={v => set("city", v)} />
                    <Field label="State" value={form.state} onChange={v => set("state", v)} />
                    <Field label="PIN Code" value={form.pincode} onChange={v => set("pincode", v)} />
                    <Field label="Aadhaar Number *" value={form.aadharNumber} onChange={v => set("aadharNumber", v)} placeholder="12-digit number" />
                    <Field label="Reference Name (if any)" value={form.refName} onChange={v => set("refName", v)} />
                    <Field label="Reference Contact (if any)" value={form.refContact} onChange={v => set("refContact", v)} />
                  </div>

                  <div className="reg-nav-row">
                    <button className="reg-btn-primary" onClick={nextStep}>Next: Upload Documents <ArrowRight size={16} /></button>
                  </div>
                </>
              )}

              {/* ── STEP 2 ── */}
              {step === 2 && (
                <>
                  <UploadField label="Aadhaar Card — Front *" hook={aadharFront} />
                  <UploadField label="Aadhaar Card — Back *" hook={aadharBack} />
                  <UploadField label="Passport Size Photograph *" hook={photo} />

                  <div className="reg-nav-row">
                    <button className="reg-btn-ghost" onClick={() => setStep(1)}><ArrowLeft size={15} /></button>
                    <button className="reg-btn-primary" onClick={nextStep}>Next: Payment <ArrowRight size={16} /></button>
                  </div>
                </>
              )}

              {/* ── STEP 3 ── */}
              {step === 3 && (
                <>
                  <div className="reg-benefit-box">
                    <div className="reg-fee">₹{fee} <span style={{ fontSize: 15, color: "#78716c", fontWeight: 700 }}>/ Year</span></div>
                    <div className="reg-fee-type">{TYPE_LABELS[type]} annual registration</div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#78716c", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>
                      What you receive
                    </div>
                    {["Welcome Kit", "One HTS Trust T-Shirt", "HTS Trust ID Card"].map(b => (
                      <div key={b} className="reg-benefit-item"><Award size={14} color="#f59e0b" />{b}</div>
                    ))}
                  </div>

                  <div className="reg-note-box">
                    <strong style={{ color: "#ef4444" }}>Important Note:</strong> Your application will be
                    accepted only after confirmation and verification by the HTS Trust backend team.
                    The annual registration fee of ₹{fee}/year is non-refundable if the application fails
                    verification or is rejected. Once approved, your registration is valid for one year
                    and must be renewed annually at the applicable fee.
                  </div>

                  <label className="reg-checkbox-row">
                    <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: 2 }} />
                    <span style={{ fontSize: 13, color: "#a8a29e" }}>I have read and agree to the above terms.</span>
                  </label>

                  <div className="reg-pay-embed">
                    <RazorpayButton key={type} buttonId={PAYMENT_BUTTON_IDS[type]} />
                  </div>

                  <label className="reg-checkbox-row">
                    <input type="checkbox" checked={paidConfirmed} onChange={e => setPaidConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
                    <span style={{ fontSize: 13, color: "#a8a29e" }}>I have completed the ₹{fee}/year payment above.</span>
                  </label>

                  <div className="reg-nav-row">
                    <button className="reg-btn-ghost" onClick={() => setStep(2)}><ArrowLeft size={15} /></button>
                    <button className="reg-btn-primary" onClick={handleSubmit} disabled={submitting}>
                      {submitting ? "Submitting..." : "Submit Application"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div className="reg-field">
      <label className="reg-label">{label}</label>
      <input className="reg-input" type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function UploadField({ label, hook }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div className="reg-upload-label">{label}</div>
      <div className={`reg-upload-box${hook.preview ? " has-file" : ""}`} onClick={() => document.getElementById(`file-${label}`).click()}>
        {hook.preview ? (
          <img src={hook.preview} alt={label} className="reg-upload-preview" />
        ) : (
          <div style={{ textAlign: "center", padding: 16 }}>
            <Upload size={20} color="#52504d" style={{ marginBottom: 6 }} />
            <div style={{ fontSize: 11, color: "#52504d" }}>Click to upload · Max 150KB · JPG/PNG</div>
          </div>
        )}
      </div>
      {hook.error && <div className="reg-upload-error">⚠️ {hook.error}</div>}
      <input id={`file-${label}`} type="file" accept="image/*" onChange={hook.handleChange} style={{ display: "none" }} />
    </div>
  );
}